/**
 * usePlzAutocomplete — Phase 21β-5c
 *
 * Wiederverwendbarer Hook für die PLZ→Ort-Auflösung. Wird in allen Adress-Formularen
 * (Properties, Contacts, Tenancy-Adressen, Einheiten etc.) genutzt.
 *
 * Verwendung:
 *
 *   const { hit } = usePlzAutocomplete(zip, (city) => setCity(city));
 *   {hit && <PlzAutocompleteHint hit={hit} />}
 *
 * - Triggert sich erst ab 5-stelliger PLZ
 * - Schreibt city nur, wenn aktuelles city-Feld leer ist (User-Eingabe nicht überschreiben)
 * - 250ms Debounce
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";

export function usePlzAutocomplete(zip, onCity, currentCity = "") {
  const [hit, setHit] = useState(null);
  useEffect(() => {
    const z = (zip || "").replace(/\D/g, "");
    if (z.length !== 5) { setHit(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/properties/lookup-plz", { params: { plz: z } });
        if (data?.found) {
          setHit(data);
          if (!currentCity && typeof onCity === "function") onCity(data.city);
        } else {
          setHit(null);
        }
      } catch {
        setHit(null);
      }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zip]);
  return { hit };
}

export function PlzAutocompleteHint({ hit, showKfz = false }) {
  if (!hit) return null;
  return (
    <div className="text-[11px] text-emerald-700 mt-1" data-testid="plz-autocomplete-hint">
      ✓ PLZ {hit.plz} · {hit.city}
      {showKfz && (
        <> · Ortskennung <span className="font-mono font-semibold">{hit.kfz}</span></>
      )}
    </div>
  );
}
